import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/client";
import TicketMergeModal from "../components/TicketMergeModal";
import { useAuth } from "../context/AuthContext";
import {
  HoldCategory,
  Ticket,
  TicketComment,
  TicketPriority,
  TicketSLAStatus,
  TicketStatus,
  TimeEntry,
  User as AuthUser,
} from "../types";
import { ActiveClock } from "./Tickets";
import AIAssistPanel from "../components/AIAssistPanel";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";
import TicketAttachments from "../components/TicketAttachments";
import TemplatePicker from "../components/TemplatePicker";
import { formatDateTime } from "../utils/datetime";

const STATUSES: TicketStatus[] = ["open", "in_progress", "on_hold", "resolved", "closed"];
const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolved: "Resolved",
  closed: "Closed",
};
const STATUS_COLORS: Record<TicketStatus, string> = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  on_hold: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  closed: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};
const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  medium: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

function fmtMinutes(mins?: number) {
  if (mins == null) return "—";
  return `${(mins / 60).toFixed(2)} hr`;
}

/** Status of one SLA dimension (response or resolution). The three
 * states the UI cares about: already met / breached / on-the-clock.
 * "ok" is the on-the-clock-and-fine case; "at_risk" is on-the-clock
 * but past the 80% threshold. */
function slaPill({
  label,
  pct,
  target,
  elapsed,
  metAt,
  breachedAt,
  atRisk,
}: {
  label: string;
  pct: number;
  target: number;
  elapsed: number;
  metAt: string | null;
  breachedAt: string | null;
  atRisk: boolean;
}) {
  let state: "met" | "breached" | "at_risk" | "ok";
  let color: string;
  if (breachedAt) {
    state = "breached";
    color =
      "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700";
  } else if (metAt) {
    state = "met";
    color =
      "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
  } else if (atRisk) {
    state = "at_risk";
    color =
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700";
  } else {
    state = "ok";
    color =
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700";
  }
  const pctText = `${Math.round(pct * 100)}%`;
  const elapsedText = `${Math.round(elapsed)} / ${target} min`;
  return (
    <div className={`border rounded-lg px-3 py-2 ${color} flex-1 min-w-[180px]`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase">{label}</div>
        <div className="text-[10px] font-mono opacity-70">{pctText}</div>
      </div>
      <div className="text-xs mt-0.5">
        {state === "breached" && "Breached"}
        {state === "met" && "Met"}
        {state === "at_risk" && "At risk"}
        {state === "ok" && "On track"}
      </div>
      <div className="text-[11px] opacity-80 mt-0.5 font-mono">{elapsedText}</div>
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "technician";

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [holdCategories, setHoldCategories] = useState<HoldCategory[]>([]);
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [running, setRunning] = useState<TimeEntry | null>(null);
  const [sla, setSla] = useState<TicketSLAStatus | null>(null);

  const [newComment, setNewComment] = useState("");
  // Audience for the next message. Explicit rather than a checkbox: an
  // unticked "internal" box is how a private note reaches a customer.
  const [commentInternal, setCommentInternal] = useState(false);
  const [statusChange, setStatusChange] = useState<{
    status: TicketStatus;
    hold_category_id: number;
  } | null>(null);
  const [timerDesc, setTimerDesc] = useState("");
  const [timerBillable, setTimerBillable] = useState(true);
  const [error, setError] = useState("");
  /**
   * Whether the merge picker is open.
   *
   * WITH THE OTHER HOOKS ON PURPOSE. It first went where the old mergeInto
   * function had been, which is below the component's early returns - so on a
   * render that bailed out early this useState never ran, and React's hook
   * order changed between renders. tsc was perfectly happy; the lint rule
   * react-hooks/rules-of-hooks caught it, and CRA treats that as a build
   * failure, which is the only reason it did not ship.
   *
   * REPLACES a window.prompt for the ticket number and a window.confirm. The
   * old comment argued a technician already has the other ticket open and can
   * read the number off a tab - reasonable, and it did not survive contact
   * with use: the owner of the product asked for ticket merging as a NEW
   * feature, which is what an undiscoverable one looks like from outside. The
   * picker also makes room for what a prompt could never show - an inventory
   * of what is about to move, for an action that cannot be undone here.
   */
  const [showMerge, setShowMerge] = useState(false);
  const [editingNotify, setEditingNotify] = useState(false);
  const [notifyDraft, setNotifyDraft] = useState("");
  const entrySort = useSort("started_at", "desc");

  const load = async () => {
    const [t, c, hc, u, e, r, s] = await Promise.all([
      api.get(`/api/v1/tickets/${id}`),
      api.get(`/api/v1/tickets/${id}/comments`),
      api.get(`/api/v1/tickets/hold-categories`),
      // /users is admin-only; /users/assignable is the staff-wide list. Reading
      // the admin route here meant a technician got a 403 that this .catch
      // turned into an empty assignee dropdown with no error shown.
      api.get(`/api/v1/users/assignable`).catch(() => ({ data: [] })),
      api.get(`/api/v1/time-entries`, { params: { ticket_id: id } }),
      api.get(`/api/v1/time-entries`, { params: { running_only: true } }),
      api.get(`/api/v1/tickets/${id}/sla`).catch(() => ({ data: null })),
    ]);
    setTicket(t.data);
    setComments(c.data);
    setHoldCategories(hc.data);
    setUsers(u.data);
    setEntries(e.data);
    setRunning(r.data.find((x: TimeEntry) => x.ticket_id === Number(id)) || null);
    setSla(s.data);
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  if (!ticket) return <div className="text-gray-400 dark:text-gray-500 p-8">Loading...</div>;

  const update = async (patch: Partial<Ticket> & { hold_category_id?: number | null }) => {
    setError("");
    try {
      const r = await api.put(`/api/v1/tickets/${id}`, patch);
      setTicket(r.data);
      setStatusChange(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Update failed");
    }
  };

  const requestStatusChange = (status: TicketStatus) => {
    if (status === "on_hold") {
      setStatusChange({ status, hold_category_id: ticket.hold_category_id || 0 });
    } else {
      update({ status });
    }
  };

  const confirmHold = () => {
    if (!statusChange?.hold_category_id) {
      setError("Pick a hold category");
      return;
    }
    update({ status: "on_hold", hold_category_id: statusChange.hold_category_id });
  };

  const submitComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    await api.post(`/api/v1/tickets/${id}/comments`, {
      body,
      is_internal: commentInternal,
    });
    setNewComment("");
    load();
  };

  const toggleMute = async () => {
    if (!ticket) return;
    const endpoint = ticket.is_muted ? "unmute" : "mute";
    if (!ticket.is_muted) {
      if (
        !window.confirm(
          "Mute this ticket? Customer-side emails (notify_email, contact_email, portal users) will be suppressed for all future updates. Internal/tech notifications still fire.",
        )
      )
        return;
    }
    const r = await api.post(`/api/v1/tickets/${id}/${endpoint}`);
    setTicket(r.data);
  };

  const startTimer = async () => {
    await api.post(`/api/v1/time-entries/start`, {
      customer_id: ticket.customer_id,
      ticket_id: ticket.id,
      description: timerDesc || `Working on #${ticket.id}: ${ticket.title}`,
      billable: timerBillable,
    });
    setTimerDesc("");
    load();
  };
  const stopTimer = async () => {
    if (!running) return;
    await api.post(`/api/v1/time-entries/${running.id}/stop`);
    load();
  };

  return (
    <div>
      <button
        onClick={() => navigate("/tickets")}
        className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4 inline-block"
      >
        &larr; All tickets
      </button>

      {/* A merged ticket is kept so old links and quoted ticket numbers still
          resolve, which means someone WILL land here. Say plainly that the
          conversation moved, at the top, before they start reading a thread
          that stopped days ago. */}
      {ticket.merged_into_ticket_id && (
        <div className="mb-4 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-500/10 px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-lg leading-none">⇄</span>
          <span className="text-sm text-violet-900 dark:text-violet-200">
            This ticket was merged into{" "}
            <Link
              to={`/tickets/${ticket.merged_into_ticket_id}`}
              className="font-semibold underline"
            >
              #{ticket.merged_into_ticket_id}
            </Link>
            . Its replies, files and logged time moved there — continue the work on that
            ticket.
          </span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-mono">
              Ticket #{ticket.id}
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{ticket.title}</h1>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <Link
                to={`/customers/${ticket.customer_id}`}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {ticket.customer_name}
              </Link>
              {ticket.device_hostname && (
                <>
                  {" "}
                  ·{" "}
                  <Link
                    to={`/devices/${ticket.device_id}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {ticket.device_hostname}
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-1 items-end">
              <div className="flex items-center gap-2">
                {ticket.is_muted && (
                  <span
                    className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 font-medium"
                    title="Customer notifications suppressed"
                  >
                    🔇 Muted
                  </span>
                )}
                <span
                  className={`text-xs px-2 py-1 rounded-full capitalize ${PRIORITY_COLORS[ticket.priority]}`}
                >
                  {ticket.priority}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[ticket.status]}`}>
                  {STATUS_LABEL[ticket.status]}
                  {ticket.status === "on_hold" &&
                    ticket.hold_category_name &&
                    ` · ${ticket.hold_category_name}`}
                </span>
              </div>
              <ActiveClock ticket={ticket} large />
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="mt-4 flex items-center gap-2">
            {!ticket.waiting_on_customer &&
              ticket.status !== "resolved" &&
              ticket.status !== "closed" && (
                <button
                  onClick={async () => {
                    const r = await api.post(`/api/v1/tickets/${ticket.id}/wait-for-customer`);
                    setTicket(r.data);
                    load();
                  }}
                  className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700"
                >
                  ⏸ Waiting on customer
                </button>
              )}
            {ticket.waiting_on_customer && (
              <button
                onClick={async () => {
                  const r = await api.post(`/api/v1/tickets/${ticket.id}/resume`);
                  setTicket(r.data);
                  load();
                }}
                className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700"
              >
                ▶ Customer responded — resume
              </button>
            )}
            {ticket.waiting_on_customer && (
              <span className="text-xs text-purple-700 bg-purple-50 dark:bg-purple-900/40 dark:text-purple-300 px-2 py-1 rounded">
                Clock paused since{" "}
                {ticket.current_status_since &&
                  new Date(ticket.current_status_since).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {ticket.description && (
          <p className="mt-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {ticket.description}
          </p>
        )}

        <div className="grid grid-cols-4 gap-3 mt-5 text-xs">
          <Info label="Opened by" value={ticket.created_by_username || "—"} />
          <Info label="Opened" value={formatDateTime(ticket.created_at)} />
          <Info label="Assignee" value={ticket.assigned_to_username || "Unassigned"} />
          <Info label="Time logged" value={fmtMinutes(ticket.time_logged_minutes)} />
        </div>

        <div className="mt-3 pt-3 border-t dark:border-gray-700 flex items-center gap-2 text-xs">
          <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Email to notify:
          </span>
          {!editingNotify ? (
            <>
              <span className="text-gray-800 dark:text-gray-200 font-mono">
                {ticket.notify_email || (
                  <em className="text-gray-400 dark:text-gray-500 not-italic">none</em>
                )}
              </span>
              {canEdit && (
                <button
                  onClick={() => {
                    setNotifyDraft(ticket.notify_email || "");
                    setEditingNotify(true);
                  }}
                  className="text-blue-500 dark:text-blue-400 hover:underline"
                >
                  Edit
                </button>
              )}
            </>
          ) : (
            <>
              <input
                type="email"
                value={notifyDraft}
                onChange={(e) => setNotifyDraft(e.target.value)}
                placeholder="user@customer.com"
                className="border rounded-lg px-2 py-1 text-xs w-72 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
              <button
                onClick={async () => {
                  await update({ notify_email: notifyDraft.trim() || (null as any) });
                  setEditingNotify(false);
                }}
                className="bg-blue-600 text-white text-xs px-2 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => setEditingNotify(false)}
                className="border dark:border-gray-600 dark:text-gray-200 text-xs px-2 py-1 rounded"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {canEdit && (
          <div className="mt-5 pt-4 border-t dark:border-gray-700 flex flex-wrap items-center gap-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">Move to:</div>
            {STATUSES.filter((s) => s !== ticket.status).map((s) => (
              <button
                key={s}
                onClick={() => requestStatusChange(s)}
                className="text-xs border border-gray-300 dark:border-gray-600 dark:text-gray-200 px-3 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
            <button
              onClick={toggleMute}
              className={`text-xs border px-3 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                ticket.is_muted
                  ? "border-gray-500 bg-gray-100 text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-200"
                  : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
              }`}
              title={
                ticket.is_muted
                  ? "Customer notifications are suppressed. Click to unmute."
                  : "Mute this ticket — customer won't get update emails when you add notes."
              }
            >
              {ticket.is_muted ? "🔔 Unmute" : "🔇 Mute"}
            </button>
            {/* Hidden once merged: merging an already-merged ticket is refused
                by the server anyway, and offering the button invites the
                attempt. */}
            {!ticket.merged_into_ticket_id && (
              <ActionButton
                onAction={async () => setShowMerge(true)}
                variant="raw"
                className="text-xs border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 px-3 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700/40"
                pendingLabel="Opening…"
                title="Merge this ticket into another — for when the same problem was reported twice."
              >
                ⇄ Merge
              </ActionButton>
            )}
            <div className="ml-auto flex items-center gap-2 text-xs">
              <label className="text-gray-500 dark:text-gray-400">Assignee:</label>
              <select
                value={ticket.assigned_to_user_id || 0}
                onChange={(e) =>
                  update({ assigned_to_user_id: Number(e.target.value) || (null as any) })
                }
                className="border rounded-lg px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              >
                <option value={0}>Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
              <label className="text-gray-500 dark:text-gray-400 ml-2">Priority:</label>
              <select
                value={ticket.priority}
                onChange={(e) => update({ priority: e.target.value as TicketPriority })}
                className="border rounded-lg px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
      </div>

      {sla && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">SLA</h3>
          <div className="flex flex-wrap gap-3">
            {slaPill({
              label: "Response",
              pct: sla.response_pct_consumed,
              target: sla.response_target_minutes,
              elapsed: sla.response_elapsed_minutes,
              metAt: sla.response_met_at,
              breachedAt: sla.response_breached_at,
              atRisk: sla.response_at_risk,
            })}
            {slaPill({
              label: "Resolution",
              pct: sla.resolution_pct_consumed,
              target: sla.resolution_target_minutes,
              elapsed: sla.resolution_elapsed_minutes,
              metAt: sla.resolution_met_at,
              breachedAt: sla.resolution_breached_at,
              atRisk: sla.resolution_at_risk,
            })}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
            Active time only — on-hold periods don't count. Response is met when a tech first
            comments; resolution is met when status moves to Resolved or Closed.
          </p>
        </div>
      )}

      {/* Time on this ticket */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Time on this ticket
          </h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Total: <strong>{fmtMinutes(ticket.time_logged_minutes)}</strong>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {running ? (
              <>
                <span className="text-xs text-yellow-700 bg-yellow-50 dark:bg-yellow-900/40 dark:text-yellow-300 px-2 py-1 rounded">
                  Timer running since {new Date(running.started_at).toLocaleTimeString()}
                </span>
                <ActionButton onAction={() => stopTimer()} variant="raw" className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg" pendingLabel="Working…">
                  Stop Timer
                </ActionButton>
              </>
            ) : (
              <>
                <input
                  placeholder="What are you working on?"
                  value={timerDesc}
                  onChange={(e) => setTimerDesc(e.target.value)}
                  className="flex-1 min-w-[200px] border rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
                <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={timerBillable}
                    onChange={(e) => setTimerBillable(e.target.checked)}
                  />
                  Billable
                </label>
                <ActionButton onAction={() => startTimer()} variant="raw" className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg" pendingLabel="Working…">
                  Start Timer
                </ActionButton>
              </>
            )}
          </div>
        )}
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <SortableTh label="When" sortKey="started_at" sort={entrySort.sort} onSort={entrySort.toggle} />
              <SortableTh label="Tech" sortKey="user_username" sort={entrySort.sort} onSort={entrySort.toggle} />
              <SortableTh label="Description" sortKey="description" sort={entrySort.sort} onSort={entrySort.toggle} />
              <SortableTh label="Duration" sortKey="duration_minutes" sort={entrySort.sort} onSort={entrySort.toggle} align="right" />
              <SortableTh label="Billable" sortKey="billable" sort={entrySort.sort} onSort={entrySort.toggle} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-2 py-4 text-center text-gray-400 dark:text-gray-500">
                  No time logged yet.
                </td>
              </tr>
            )}
            {sortRows(entries, entrySort.sort, (e, key) => {
              switch (key) {
                case "user_username":
                  return (e.user_username || "").toLowerCase();
                case "description":
                  return (e.description || "").toLowerCase();
                case "duration_minutes":
                  return e.duration_minutes || 0;
                case "billable":
                  return e.billable ? 1 : 0;
                default:
                  return e.started_at ? Date.parse(e.started_at) : 0;
              }
            }).map((e) => (
              <tr key={e.id}>
                <td className="px-2 py-1 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {formatDateTime(e.started_at)}
                </td>
                <td className="px-2 py-1 dark:text-gray-200">{e.user_username || "—"}</td>
                <td className="px-2 py-1 dark:text-gray-200">{e.description || "—"}</td>
                <td className="px-2 py-1 text-right dark:text-gray-200">
                  {e.ended_at ? (
                    fmtMinutes(e.duration_minutes)
                  ) : (
                    <span className="text-yellow-700 dark:text-yellow-400">running…</span>
                  )}
                </td>
                <td className="px-2 py-1 text-center dark:text-gray-200">
                  {e.billable ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI Assist */}
      <div className="mb-4">
        <AIAssistPanel
          ticketId={ticket.id}
          onActioned={load}
          onUseDraft={(text) => {
            // Same contract as the canned-reply picker: appended, never
            // replacing, and it does NOT flip the internal-note toggle — an AI
            // customer reply is public by nature and silently making it
            // internal would swallow the answer.
            setNewComment((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${text}` : text));
            setCommentInternal(false);
          }}
        />
      </div>

      {/* Attachments. Above the thread rather than below it: a file is
          evidence about the problem, and a tech scanning the ticket should see
          that a screenshot exists before reading twenty replies about it. */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Attachments</h3>
        <TicketAttachments
          client={api}
          basePath={`/api/v1/tickets/${ticket.id}`}
          canDelete={canEdit}
          canUpload={canEdit}
        />
      </div>

      {/* Comments */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Comments ({comments.length})
        </h3>
        <div className="space-y-3 mb-4">
          {comments.length === 0 && (
            <p className="text-center text-gray-400 dark:text-gray-500 text-sm py-2">
              No comments yet.
            </p>
          )}
          {comments.map((c) => (
            <div
              key={c.id}
              className={
                c.is_internal
                  ? // Amber and dashed: a technician scanning the thread has to
                    // be able to tell instantly what the customer has NOT seen,
                    // or they repeat themselves — or worse, assume the customer
                    // knows something nobody told them.
                    "border border-dashed border-amber-300 dark:border-amber-700 rounded-lg p-3 bg-amber-50/60 dark:bg-amber-500/5"
                  : "border dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900/50"
              }
            >
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2 flex-wrap">
                <strong className="text-gray-700 dark:text-gray-300">
                  {c.author_username || "—"}
                </strong>
                <span>{formatDateTime(c.created_at)}</span>
                {c.is_internal && (
                  <span className="rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-[11px] font-medium">
                    Internal — not sent to the customer
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {c.body}
              </div>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex flex-col gap-2">
            {/* Audience first, then the text. Deciding who receives it before
                writing it is the right order, and it means the placeholder and
                the send button can both say what will happen. */}
            <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden self-start text-sm">
              <button
                type="button"
                onClick={() => setCommentInternal(false)}
                className={
                  !commentInternal
                    ? "px-3 py-1.5 bg-blue-600 text-white"
                    : "px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                }
              >
                Reply to customer
              </button>
              <button
                type="button"
                onClick={() => setCommentInternal(true)}
                className={
                  commentInternal
                    ? "px-3 py-1.5 bg-amber-500 text-white"
                    : "px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                }
              >
                Internal note
              </button>
              </div>
              <TemplatePicker
                ticketId={ticket.id}
                onInsert={(body, defaultInternal) => {
                  // Appended, never replacing. Silently discarding text somebody
                  // already typed is the kind of thing you only forgive once.
                  setNewComment((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${body}` : body));
                  // The audience travels with the template: an escalation note
                  // is internal by nature, and having to remember to switch is
                  // how one gets sent to a customer.
                  if (defaultInternal) setCommentInternal(true);
                }}
              />
            </div>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
              placeholder={
                commentInternal
                  ? "Note for the team — the customer will not see this…"
                  : "Reply — this is emailed to the customer…"
              }
              className={
                commentInternal
                  ? "w-full border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-500/5 rounded-lg px-3 py-2 text-sm dark:text-gray-100"
                  : "w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              }
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {commentInternal
                  ? "Visible to your team only. The assigned technician is notified."
                  : "Emailed to the customer's ticket contacts."}
              </span>
              <ActionButton
                onAction={() => submitComment()}
                variant="raw"
                className={
                  commentInternal
                    ? "bg-amber-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-amber-600"
                    : "bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
                }
                pendingLabel={commentInternal ? "Saving…" : "Sending…"}
              >
                {commentInternal ? "Save note" : "Send reply"}
              </ActionButton>
            </div>
          </div>
        )}
      </div>

      {statusChange?.status === "on_hold" && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-2 dark:text-gray-100">Put ticket on hold</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Pick a reason — it shows up on the ticket and in reports.
            </p>
            <select
              value={statusChange.hold_category_id}
              onChange={(e) =>
                setStatusChange({ ...statusChange, hold_category_id: Number(e.target.value) })
              }
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            >
              <option value={0}>Select…</option>
              {holdCategories.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStatusChange(null)}
                className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmHold}
                className="bg-purple-600 text-white text-sm px-4 py-2 rounded-lg"
              >
                Put on hold
              </button>
            </div>
          </div>
        </div>
      )}

      {showMerge && ticket && (
        <TicketMergeModal
          sourceId={Number(id)}
          sourceTitle={ticket.title}
          onClose={() => setShowMerge(false)}
          onMerged={(survivorId) => {
            setShowMerge(false);
            // Land on the ticket the work actually moved to, which is where
            // the technician needs to be. Not necessarily the one they
            // clicked - a merge follows the chain.
            navigate(`/tickets/${survivorId}`);
          }}
        />
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-gray-500 dark:text-gray-400 uppercase text-[10px]">{label}</div>
      <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">{value}</div>
    </div>
  );
}
